import type React from 'react';

import { Box, Divider, FormField, FormHelp, Input, Label, Text, Textarea } from '@zillow/constellation-10';

import OptionSelector from './OptionSelector';
import HeaderToggle from './HeaderToggle';
import type { Section } from '../../../../models';
import { ELECTRICITY } from '../../../../constants';
import { isMultiFamily } from './MultiFamilyLandlordUtility';
import YesAndNo from '../../../Widgets/YesAndNo';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

export default function Electricity_v3({ section, setSectionValues }: Props): JSX.Element {
  function setLandlordResponsibility(): void {
    setSectionValues({
      [ELECTRICITY.FIELD_NAME.TENANT_RESPONSIBILITY]: false,
      [ELECTRICITY.FIELD_NAME.LANDLORD_RESPONSIBILITY]: true,
      [ELECTRICITY.FIELD_NAME.MULTIFAMILY_TENANT_RESPONSIBILITY.DELIVERED_TO_AREAS_OTHER_THAN_TENANTS_UNIT]: null,
    });
  }

  function setTenantResponsibility(): void {
    setSectionValues({
      [ELECTRICITY.FIELD_NAME.TENANT_RESPONSIBILITY]: true,
      [ELECTRICITY.FIELD_NAME.LANDLORD_RESPONSIBILITY]: false,
      [ELECTRICITY.FIELD_NAME.MULTIFAMILY_LANDLORD_RESPONSIBILITY.BILL_AMOUNT]: '',
    });
  }

  function setServiceProvider(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    setSectionValues({
      [ELECTRICITY.FIELD_NAME.SERVICE_PROVIDER]: event.currentTarget.value,
    });
  }

  function setBillAmount(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    setSectionValues({
      [ELECTRICITY.FIELD_NAME.MULTIFAMILY_LANDLORD_RESPONSIBILITY.BILL_AMOUNT]: event.currentTarget.value,
    });
  }

  function toggleUtility(): void {
    setSectionValues({
      [ELECTRICITY.FIELD_NAME.IS_ENABLED]: !section.getFieldValue(ELECTRICITY.FIELD_NAME.IS_ENABLED),
    });
  }

  function yesDeliveredToAreas(): void {
    setSectionValues({
      [ELECTRICITY.FIELD_NAME.MULTIFAMILY_TENANT_RESPONSIBILITY.DELIVERED_TO_AREAS_OTHER_THAN_TENANTS_UNIT]: true,
    });
  }

  function noDeliveredToAreas(): void {
    setSectionValues({
      [ELECTRICITY.FIELD_NAME.MULTIFAMILY_TENANT_RESPONSIBILITY.DELIVERED_TO_AREAS_OTHER_THAN_TENANTS_UNIT]: false,
    });
  }

  const isMultiFamilyLandlord = isMultiFamily(section);
  const electricity_isEnabled = section.getFieldValue(ELECTRICITY.FIELD_NAME.IS_ENABLED);
  const tenantSelected = section.getFieldValue(ELECTRICITY.FIELD_NAME.TENANT_RESPONSIBILITY);
  const landlordSelected = section.getFieldValue(ELECTRICITY.FIELD_NAME.LANDLORD_RESPONSIBILITY);
  const serviceProvider = section.getFieldValue(ELECTRICITY.FIELD_NAME.SERVICE_PROVIDER);

  return (
    <>
      <HeaderToggle
        header="Electricity"
        subheader="Tenant pays in addition to rent"
        switchOn={electricity_isEnabled}
        onSwitchToggle={toggleUtility}
      />
      {electricity_isEnabled && (
        <>
          <Divider css={{ marginTop: 'layout.tighter', marginBottom: 'layout.tighter' }} />
          <Box>
            <OptionSelector
              disabled={!electricity_isEnabled}
              optionsText="Who will be responsible for electricity set up?"
              option1Content="Responsible for account set up and direct payments to specified service provider."
              option2Content="Responsible for account set up and will bill tenant separately."
              option1Selected={tenantSelected}
              onOption1Click={setTenantResponsibility}
              option2Selected={landlordSelected}
              onOption2Click={setLandlordResponsibility}
            />
          </Box>

          {tenantSelected && (
            <>
              <FormField
                required
                css={{ marginBottom: isMultiFamilyLandlord ? 'sm' : '' }}
                label={<Label id="tenantSelected">What service provider?</Label>}
                control={
                  <Input
                    data-test="tenantSelectedInput"
                    aria-labelledby="tenantSelected"
                    value={serviceProvider}
                    onChange={setServiceProvider}
                  />
                }
              />
            </>
          )}

          {isMultiFamilyLandlord && tenantSelected && (
            <>
              <Text>Will this electricity service be delivered to areas other than the tenant’s unit?</Text>
              <YesAndNo
                yesActive={section.getFieldValue(
                  ELECTRICITY.FIELD_NAME.MULTIFAMILY_TENANT_RESPONSIBILITY.DELIVERED_TO_AREAS_OTHER_THAN_TENANTS_UNIT,
                )}
                yesHandler={yesDeliveredToAreas}
                noHandler={noDeliveredToAreas}
              />
            </>
          )}

          {isMultiFamilyLandlord && landlordSelected && (
            <FormField
              required
              label={
                <Label id="landlordSelected">
                  How will you determine the amount to bill the tenant for electricity?
                </Label>
              }
              control={
                <Textarea
                  aria-labelledby="landlordSelected"
                  onChange={setBillAmount}
                  value={section.getFieldValue(ELECTRICITY.FIELD_NAME.MULTIFAMILY_LANDLORD_RESPONSIBILITY.BILL_AMOUNT)}
                  css={{ resize: 'vertical' }}
                />
              }
              description={
                <FormHelp>
                  Example: The provider bills me per kWh used by the entire building. I distribute that cost evenly
                  among each of the building’s 5 units. Each unit pays 1/5 of the total cost.
                </FormHelp>
              }
            />
          )}
        </>
      )}
    </>
  );
}
